/**
 * Image component that produces a low quality placeholder image using Fastly's
 * query param API. This image is shown while the main image loads in. When the
 * main image has finished loading, the placeholder fades out.
 *
 * Because the placeholder cannot know the main image's intrinsic size, that
 * information must be provided via props, specifically the `placeholderImage`
 * prop.
 *
 * The placeholder purposely ignores click events so users can interact with
 * the main image (open in a new tab, download, copy, etc.).
 */

import { createComponent } from '@carvana/merch-components';
import cx from 'classnames';
import React, { forwardRef, useMemo, useState } from 'react';
import optimizeImage from '~/utilities/optimizeImage';

export type ImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  containerClassName?: string;

  height: number;

  /**
   * Attributes to pass to placeholder image.
   */
  placeholderImage?: React.ImgHTMLAttributes<HTMLImageElement> & {
    /**
     * Amount to blur the placeholder image.
     */
    blurAmount?: number;

    containerClassName?: string;

    height?: number;

    /**
     * (Optional) Height to pass to Fastly. Defaults to main image height.
     * Value will be divided by two.
     */
    urlHeight?: number;

    /**
     * (Optional) Width to pass to Fastly. Defaults to main image width.
     * Value will be divided by two.
     */
    urlWidth?: number;

    width?: number;
  };

  /**
   * Whether main image should load or not. Useful when lazy loading images.
   */
  shouldLoad?: boolean;

  width: number;

  wrapperClassName?: string;
};

const ImageContainer = createComponent('div', {
  className:
    'col-span-full row-span-full flex overflow-hidden transition-all duration-500 -:opacity-0'
});

function generatePlaceholderUrl(
  imageUrl: string,
  blurAmount: number,
  height: number,
  width: number
): string {
  return imageUrl.toLowerCase().includes('fastly')
    ? optimizeImage(imageUrl, {
        blur: blurAmount,
        height: height / 2,
        optimize: 'high',
        width: width / 2
      })
    : '';
}

const dummyFn = () => {};

export default forwardRef<HTMLImageElement, ImageProps>(function Image(
  {
    alt,
    className: imageClassName,
    containerClassName: imageContainerClassName,
    height,
    width,
    onLoad = dummyFn,
    placeholderImage: {
      blurAmount = 25,
      className: placeholderClassName = imageClassName,
      containerClassName:
        placeholderContainerClassName = imageContainerClassName,
      height: placeholderHeight = height,
      width: placeholderWidth = width,
      urlHeight = placeholderHeight,
      urlWidth = placeholderWidth,
      ...placeholderImage
    } = {},
    shouldLoad = true,
    src = '',
    wrapperClassName,
    ...props
  }: ImageProps,
  ref
) {
  const placeholderUrl = useMemo(
    () => generatePlaceholderUrl(src, blurAmount, urlHeight, urlWidth),
    [blurAmount, src, urlHeight, urlWidth]
  );

  const [isLoaded, setLoaded] = useState(false);

  const showImage = shouldLoad || isLoaded;
  const showPlaceholderImage = Boolean(placeholderUrl);

  return src ? (
    <div className={cx('grid max-h-full w-fit max-w-full', wrapperClassName)}>
      {showImage && (
        <ImageContainer
          className={cx(
            imageContainerClassName,
            isLoaded ? 'opacity-100' : undefined
          )}
        >
          <img
            {...props}
            ref={ref}
            src={src}
            onLoad={e => {
              setLoaded(true);
              onLoad(e);
            }}
            alt={alt}
            className={imageClassName}
            height={height}
            width={width}
          />
        </ImageContainer>
      )}
      <ImageContainer
        className={cx('pointer-events-none', placeholderContainerClassName, {
          'opacity-100': !isLoaded
        })}
      >
        {showPlaceholderImage && (
          <img
            {...placeholderImage}
            src={placeholderUrl}
            alt={alt}
            className={placeholderClassName}
            height={height}
            width={width}
          />
        )}
      </ImageContainer>
    </div>
  ) : null;
});
